import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Typography, TypographyProps } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import React, { useEffect, useRef, useState } from 'react';
import { ENTER_KEY, ESCAPE_KEY } from './event-keys';
import { isNotNullOrUndefined, isString } from '@vy/utils';

export type EditableLabelRenderProps<ValueType> = {
  value: ValueType;
  onChange: (value: ValueType) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLElement>;
};

type EditableLabelWrapperProps<ValueType> = {
  label: React.ReactNode;
  value: ValueType;
  onSave: (value: ValueType) => Promise<void>;
  displayInline?: boolean;
  labelText?: React.ReactNode;
  renderInput: (props: EditableLabelRenderProps<ValueType>) => React.ReactNode;
  renderDisplayValue?: (value: ValueType) => React.ReactNode;
  validateValue?: (value: ValueType) => boolean;
  disabled?: boolean;
} & TypographyProps;

export const EditableLabelWrapper = <T,>({
  label,
  value: initialValue,
  onSave,
  displayInline = false,
  labelText,
  renderInput,
  renderDisplayValue,
  validateValue,
  disabled = false,
  ...typographyProps
}: EditableLabelWrapperProps<T>): React.ReactElement => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [value, setValue] = useState<T>(initialValue);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if ('select' in inputRef.current && typeof inputRef.current.select === 'function') {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleSave = async () => {
    const isValid = validateValue ? validateValue(value) : true;

    if (isValid) {
      await onSave(value);
    }

    handleCancel();
  };

  const handleCancel = () => {
    setValue(initialValue);
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ENTER_KEY) {
      void handleSave();
    }
    if (event.key === ESCAPE_KEY) {
      handleCancel();
    }
  };

  const renderLabel = () => {
    if (!displayInline || !labelText) {
      return null;
    }

    return isString(labelText) ? (
      <Typography
        variant="subtitle2"
        color="textSecondary"
        sx={{
          fontSize: "14px",
          marginRight: 1
        }}>
        {labelText}
      </Typography>
    ) : (
      <>{labelText}</>
    );
  };

  const displayValue = isNotNullOrUndefined(renderDisplayValue) ? renderDisplayValue(value) : label;

  const editableContent = !isEditing ? (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        cursor: disabled ? 'default' : 'pointer',
        height: displayInline ? 'auto' : '32px',
      }}
      onMouseEnter={() => !disabled && setIsHovering(true)}
      onMouseLeave={() => !disabled && setIsHovering(false)}
      onClick={() => !disabled && setIsEditing(true)}
    >
      <Typography {...typographyProps}>{displayValue}</Typography>

      {!disabled && (
        <IconButton size="small" sx={{ opacity: isHovering ? 1 : 0 }}>
          <VyIcon icon="Pen" />
        </IconButton>
      )}
    </Box>
  ) : (
    <Box sx={{ display: displayInline ? 'inline-flex' : 'flex', alignItems: 'center', gap: 1 }}>
      {renderInput({
        value,
        onChange: setValue,
        onKeyDown: handleKeyDown,
        inputRef,
      })}
      <IconButton onClick={handleSave} size="small">
        <CheckIcon color="success" sx={{ fontSize: '20px' }} />
      </IconButton>

      <IconButton onClick={handleCancel} size="small">
        <CloseIcon color="inherit" sx={{ fontSize: '20px' }} />
      </IconButton>
    </Box>
  );

  if (displayInline) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {renderLabel()}
        {editableContent}
      </Box>
    );
  }

  return editableContent;
};
